import { Alert } from "@/components/ui/alert";
import { Button } from "components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Separator } from "components/ui/separator";
import { Spinner } from "components/ui/spinner";
import { ReactNode } from "react";

type ButtonColor =
  | "inherit"
  | "primary"
  | "secondary"
  | "success"
  | "error"
  | "info"
  | "warning";

type ButtonMuiVariant = "text" | "outlined" | "contained";

interface ModalProps {
  isLoading: boolean;
  open: boolean;
  header: string;
  body: string | ReactNode;
  body2?: string;
  isError?: boolean;
  leftButtonText: string;
  rightButtonText: string;
  leftButtonDisabled?: boolean;
  rightButtonDisabled?: boolean;
  rightButtonVariant?: ButtonMuiVariant;
  leftButtonVariant?: ButtonMuiVariant;
  errorTitle?: string;
  errorMessage?: string;
  leftButtonColor: ButtonColor;
  rightButtonColor: ButtonColor;
  handleLeftButton: any;
  handleRightButton: any;
  handleClose: () => void;
  children?: React.ReactNode;
  isLeftButtonLoading?: boolean;
  isRightButtonLoading?: boolean;
}

const COLOR_TINT: Record<ButtonColor, string> = {
  inherit: "",
  primary: "",
  secondary: "",
  success: "text-success hover:text-success",
  error: "",
  info: "text-info hover:text-info",
  warning: "text-warning hover:text-warning",
};

const resolveButtonProps = (
  muiVariant: ButtonMuiVariant | undefined,
  color: ButtonColor,
): {
  variant: "default" | "outline" | "ghost" | "destructive";
  className: string;
} => {
  if (color === "error") {
    return { variant: "destructive", className: "" };
  }

  const variant =
    muiVariant === "text"
      ? "ghost"
      : muiVariant === "outlined"
        ? "outline"
        : "default";

  const needsTint = variant !== "default";
  return {
    variant,
    className: needsTint ? COLOR_TINT[color] : "",
  };
};

export default function ConfirmationModal(props: ModalProps) {
  return (
    <Dialog
      open={props.open}
      onOpenChange={(next) => {
        if (!next) props.handleClose();
      }}
    >
      <DialogContent showCloseButton={false} className="sm:max-w-lg">
        {props.isLoading ? (
          <LoadingContent />
        ) : props.isError ? (
          <ErrorContent {...props} />
        ) : (
          <BaseContent {...props} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function ErrorContent(props: ModalProps) {
  const errorTitle = props.errorTitle ?? "Error";
  const errorMessage =
    props.errorMessage ?? "An error occurred while processing the request.";

  return (
    <>
      <DialogHeader>
        <DialogTitle>{errorTitle}</DialogTitle>
      </DialogHeader>
      <Alert variant="destructive">{errorMessage}</Alert>
      {props.children}
      <DialogFooter>
        <Button variant="outline" size="sm" onClick={props.handleClose}>
          Close
        </Button>
      </DialogFooter>
    </>
  );
}

function BaseContent(props: ModalProps) {
  const leftBtn = resolveButtonProps(
    props.leftButtonVariant,
    props.leftButtonColor,
  );
  const rightBtn = resolveButtonProps(
    props.rightButtonVariant ?? "contained",
    props.rightButtonColor,
  );

  return (
    <>
      {props.body2 ? (
        <>
          <DialogHeader>
            <DialogTitle>{props.header}</DialogTitle>
            <DialogDescription asChild>
              <div>{props.body}</div>
            </DialogDescription>
          </DialogHeader>

          {props.children}
          <p className="text-muted-foreground">
            <b>{props.body2}</b>
          </p>
        </>
      ) : (
        <>
          <DialogHeader>
            <DialogTitle className="text-base font-semibold">
              {props.header}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-2 text-xs/relaxed">{props.body}</div>
          {props.children}
        </>
      )}

      <Separator />
      <DialogFooter>
        <Button
          variant={leftBtn.variant}
          size="sm"
          className={leftBtn.className}
          disabled={props.leftButtonDisabled ?? false}
          onClick={props.handleLeftButton}
        >
          {props.isLeftButtonLoading && <Spinner />}
          {props.leftButtonText}
        </Button>
        <Button
          variant={rightBtn.variant}
          size="sm"
          className={rightBtn.className}
          disabled={props.rightButtonDisabled ?? false}
          onClick={props.handleRightButton}
        >
          {props.isRightButtonLoading && <Spinner />}
          {props.rightButtonText}
        </Button>
      </DialogFooter>
    </>
  );
}

function LoadingContent() {
  return (
    <>
      <DialogHeader>
        <DialogTitle>Processing</DialogTitle>
      </DialogHeader>
      <div className="flex items-center gap-2">
        <Spinner />
        <p className="text-xs/relaxed">
          Please wait while your request is being processed.
        </p>
      </div>
    </>
  );
}
